import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CirclePlus, Loader, QrCode, Copy, CheckCircle, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Mask } from "@/utils/mask";
import { createPaymentAdvance, checkPaymentAdvanceStatus } from "@/Api/paymentAdvances";
import { PAYMENT_ADVANCE_STATUS } from "@/utils/constants";
import { getSelectClients } from "@/Api/clients";
import { getSellersDataWithFilters } from "@/Api/sellers";
import { PaymentAdvance, PaymentAdvanceResponse } from "@/types/paymentAdvance";
import { AsyncSearchableSelect } from "@/components/ui/async-searchable-select";
import { useAuth } from "@/hooks/auth";
import { TypeUser } from "@/types/userType";
import { AddBtn as AddClientBtn } from "@/App/Clients/Add";

interface Client {
  id: number;
  uuid: string;
  name: string;
  phone?: string;
}

interface Seller {
  id: number;
  uuid: string;
  user?: { name: string };
}

interface AddBtnProps {
  updateFront: React.Dispatch<React.SetStateAction<PaymentAdvanceResponse>>;
}

export const AddBtn = ({ updateFront }: AddBtnProps) => {
  const user = useAuth();
  const userSeller = user?.user?.seller;
  const isSellerUser = user?.user?.user_type_id === TypeUser.seller;

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedSeller, setSelectedSeller] = useState<Seller | null>(
    isSellerUser && userSeller
      ? { id: userSeller.id, uuid: "", user: { name: user?.user?.name || "" } }
      : null
  );
  const [createdPix, setCreatedPix] = useState<PaymentAdvance | null>(null);
  const [copiedQrCode, setCopiedQrCode] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const [formData, setFormData] = useState({
    amount: "",
    observation: "",
  });
  const [clientsUpdateKey, setClientsUpdateKey] = useState(0);

  useEffect(() => {
    if (open) {
      setCreatedPix(null);
      setCopiedQrCode(false);
      setCheckingStatus(false);
      setSelectedClient(null);
      if (!isSellerUser) {
        setSelectedSeller(null);
      } else if (userSeller) {
        setSelectedSeller({
          id: userSeller.id,
          uuid: "",
          user: { name: user?.user?.name || "" },
        });
      }
      setFormData({ amount: "", observation: "" });
    }
  }, [open, isSellerUser, userSeller, user?.user?.name]);

  useEffect(() => {
    if (!createdPix || createdPix.status === PAYMENT_ADVANCE_STATUS.PAID) {
      return;
    }

    const checkStatus = async () => {
      setCheckingStatus(true);
      try {
        const { data, ok } = await checkPaymentAdvanceStatus(createdPix.uuid);
        if (ok && data?.response) {
          if (data.response.status === PAYMENT_ADVANCE_STATUS.PAID) {
            toast.success("Pagamento confirmado!");
            setCreatedPix((prev) =>
              prev
                ? {
                    ...prev,
                    status: PAYMENT_ADVANCE_STATUS.PAID as PaymentAdvance["status"],
                    paid_at: data.response.paid_at,
                  }
                : null
            );
            updateFront((prev) => ({
              ...prev,
              data: prev.data.map((p) =>
                p.uuid === createdPix.uuid
                  ? {
                      ...p,
                      status: PAYMENT_ADVANCE_STATUS.PAID as PaymentAdvance["status"],
                      paid_at: data.response.paid_at,
                    }
                  : p
              ),
            }));
          }
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 5000);
    return () => clearInterval(interval);
  }, [createdPix?.uuid, createdPix?.status, updateFront]);

  const searchClients = useCallback(async (searchTerm: string): Promise<Client[]> => {
    try {
      const isPhoneSearch = /^\d+$/.test(searchTerm.replace(/[\s\-()]/g, ''));
      const params = isPhoneSearch
        ? { phone: searchTerm }
        : { name: searchTerm };
      const { data, ok } = await getSelectClients(params);
      if (ok && data?.response) {
        return data.response;
      }
      return [];
    } catch (error) {
      console.error("Error searching clients:", error);
      return [];
    }
  }, []);

  const searchSellers = useCallback(async (searchTerm: string): Promise<Seller[]> => {
    try {
      const { data, ok } = await getSellersDataWithFilters({
        name: searchTerm,
        per_page: 20,
      });
      if (ok && data?.response?.data) {
        return data.response.data;
      }
      return [];
    } catch (error) {
      console.error("Error searching sellers:", error);
      return [];
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedClient || !selectedSeller || !formData.amount) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    const amountValue = parseFloat(Mask.transformCurrencyInFloat(formData.amount));
    if (isNaN(amountValue) || amountValue <= 0) {
      toast.error("Valor inválido");
      return;
    }

    setLoading(true);
    try {
      const { data, ok } = await createPaymentAdvance({
        client_id: selectedClient.id,
        seller_id: selectedSeller.id,
        amount: amountValue,
        observation: formData.observation || undefined,
      });

      if (ok && data?.response) {
        toast.success("Pix Gerado criado com sucesso!");
        setCreatedPix(data.response);

        updateFront((prev) => ({
          ...prev,
          data: [data.response, ...prev.data],
          total: prev.total + 1,
        }));
      }
    } catch (error) {
      toast.error("Erro ao criar Pix Gerado");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyQrCode = () => {
    if (createdPix?.pix_qrcode) {
      navigator.clipboard.writeText(createdPix.pix_qrcode);
      setCopiedQrCode(true);
      toast.success("Código PIX copiado!");
      setTimeout(() => setCopiedQrCode(false), 2000);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({ amount: "", observation: "" });
    setSelectedClient(null);
    if (!isSellerUser) {
      setSelectedSeller(null);
    }
    setCreatedPix(null);
    setCopiedQrCode(false);
    setCheckingStatus(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", "bg-primary")}
        >
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Gerar Novo Pix
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {createdPix ? "Pix Gerado com Sucesso!" : "Gerar Novo Pix"}
          </DialogTitle>
        </DialogHeader>

        {createdPix ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-2" />
              <p className="text-green-700 font-medium">PIX gerado com sucesso!</p>
              <p className="text-2xl font-bold text-green-800 mt-2">
                {Mask.formatCurrency(createdPix.amount)}
              </p>
            </div>

            <div className={cn(
              "border rounded-lg p-3 text-center",
              createdPix.status === PAYMENT_ADVANCE_STATUS.PAID
                ? "bg-green-100 border-green-300"
                : "bg-yellow-50 border-yellow-300"
            )}>
              {createdPix.status === PAYMENT_ADVANCE_STATUS.PAID ? (
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-green-700 font-medium">Pagamento Confirmado!</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  {checkingStatus ? (
                    <Loader className="h-5 w-5 text-yellow-600 animate-spin" />
                  ) : (
                    <div className="h-5 w-5 rounded-full bg-yellow-500 animate-pulse" />
                  )}
                  <span className="text-yellow-700 font-medium">
                    {checkingStatus ? "Verificando pagamento..." : "Aguardando pagamento..."}
                  </span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <label className="text-gray-500">Cliente</label>
                <p className="font-medium">{createdPix.client?.name}</p>
              </div>
              <div>
                <label className="text-gray-500">Vendedor</label>
                <p className="font-medium">{createdPix.seller?.user?.name}</p>
              </div>
            </div>

            {createdPix.pix_qrcode && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <QrCode className="h-4 w-4" />
                    Código PIX Copia e Cola
                  </label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCopyQrCode}
                    className="h-8"
                  >
                    {copiedQrCode ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
                        Copiado!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1" />
                        Copiar
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-600 break-all bg-white p-2 rounded border">
                  {createdPix.pix_qrcode}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button onClick={handleClose} className={cn(colors.bg_primary)}>
                Fechar
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Cliente *</Label>
                <AddClientBtn
                  updateFront={() => setClientsUpdateKey(prev => prev + 1)}
                  customTrigger={
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                    >
                      <UserPlus className="h-3 w-3 mr-1" />
                      Novo Cliente
                    </Button>
                  }
                />
              </div>
              <AsyncSearchableSelect<Client>
                key={clientsUpdateKey}
                fetchItems={searchClients}
                displayField={(client) =>
                  client.phone
                    ? `${client.name} - ${Mask.maskPhone(client.phone)}`
                    : client.name
                }
                placeholder="Buscar cliente..."
                searchPlaceholder="Digite o nome ou telefone do cliente..."
                emptyMessage="Nenhum cliente encontrado."
                value={selectedClient}
                onSelect={setSelectedClient}
                getItemId={(item) => item.id}
              />
            </div>

            <div className="space-y-2">
              <Label>Vendedor *</Label>
              {isSellerUser ? (
                <Input
                  value={user?.user?.name || ""}
                  disabled
                  className="bg-gray-100"
                />
              ) : (
                <AsyncSearchableSelect<Seller>
                  fetchItems={searchSellers}
                  displayField={(seller) => seller.user?.name || `Vendedor #${seller.id}`}
                  placeholder="Buscar vendedor..."
                  searchPlaceholder="Digite o nome do vendedor..."
                  emptyMessage="Nenhum vendedor encontrado."
                  value={selectedSeller}
                  onSelect={setSelectedSeller}
                  getItemId={(item) => item.id}
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">Valor *</Label>
              <Input
                id="amount"
                typeMask="currency"
                value={formData.amount}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, amount: e.target.value }))
                }
                placeholder="R$ 0,00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="observation">Observação</Label>
              <Textarea
                id="observation"
                value={formData.observation}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, observation: e.target.value }))
                }
                placeholder="Observação opcional..."
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className={cn(colors.bg_primary)}
              >
                {loading ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin mr-2" />
                    Gerando...
                  </>
                ) : (
                  "Gerar Pix"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
